import Dropdown, {
  DID_NOT_FIND_VALUE,
  Option,
} from 'components/formComponents/Base/Dropdown';
import InputField from 'components/formComponents/Base/InputField';
import { useWorksites } from '../../../../ApplicationV3/hooks/useWorksites';
import { useFormContext } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '../../../../../hooks';
import { getWorksiteAlternatives } from '../../../../../Functions/Application';
import { useEffect } from 'react';
import { getWorksiteAlternative } from '../../../utils/worksiteUtils';
import { setWorksiteRegardingApplication } from '../../../../../redux/reducer/partyApplicationSlice';
import { PartyRegardingApplicationSectionTexts } from '../../../../../types/PartyApplication/PartyApplicationTexts';
import RadioButton from 'components/formComponents/Base/RadioButton';
import { WorksiteAddressCountry } from '../../../../../types/PartyApplication/partyApplication';

export const didNotFindOption: Option = {
  value: DID_NOT_FIND_VALUE,
  label: 'Hittade inte arbetsställe',
  subLabel: 'Ange arbetsställe manuellt',
  isNotFoundOption: true,
};

type Props = {
  texts?: PartyRegardingApplicationSectionTexts;
};

const WorksiteBlock = ({ texts }: Props) => {
  const dispatch = useAppDispatch();
  const {
    partyRegardingApplication: partRegardingApplication,
    worksiteRegardingApplication,
  } = useAppSelector((state) => state.partyApplication);
  const { watch, setValue } = useFormContext();
  const currentWorksiteValue = watch('worksiteRegardingApplication');
  const { worksites, loadingWorksites } = useWorksites(
    partRegardingApplication || ''
  );
  const selectedWorksite = worksites?.find(
    (w) => w.id === currentWorksiteValue
  );
  const didNotFindSelected = currentWorksiteValue === didNotFindOption.value;

  const handleNotFoundClick = () => {
    setValue('worksiteRegardingApplication', didNotFindOption.value);
  };

  useEffect(() => {
    if (selectedWorksite?.id !== worksiteRegardingApplication) {
      dispatch(setWorksiteRegardingApplication(selectedWorksite?.id || ''));
    }
  }, [selectedWorksite, worksiteRegardingApplication, dispatch]);

  useEffect(() => {
    if (
      !partRegardingApplication ||
      loadingWorksites ||
      !worksites ||
      worksites.length !== 1 ||
      selectedWorksite ||
      didNotFindSelected
    ) {
      return;
    }

    setValue('worksiteRegardingApplication', worksites[0].id, {
      shouldValidate: true,
    });
  }, [
    didNotFindSelected,
    loadingWorksites,
    partRegardingApplication,
    selectedWorksite,
    setValue,
    worksites,
  ]);

  return (
    <>
      <Dropdown
        label={texts?.worksiteRegardingApplication?.label ?? 'Arbetsställe'}
        placeholder={
          texts?.worksiteRegardingApplication?.placeholder ??
          'Välj ett alternativ'
        }
        name="worksiteRegardingApplication"
        options={getWorksiteAlternatives(worksites)}
        value={
          selectedWorksite
            ? getWorksiteAlternative(selectedWorksite)
            : didNotFindSelected
              ? didNotFindOption
              : null
        }
        showNotFoundOption={true}
        notFoundOption={didNotFindOption}
        onNotFoundClick={handleNotFoundClick}
        isLoading={loadingWorksites}
      />

      {didNotFindSelected && (
        <>
          <InputField
            name="worksitename"
            label={texts?.worksitename?.label ?? 'Arbetsställets namn'}
            placeholder={texts?.worksitename?.placeholder ?? 'Ange namn'}
            required
          />
          <RadioButton
            name="worksiteAddressCountry"
            options={[
              {
                id: 'worksiteAddressCountrySwedish',
                displayValue:
                  texts?.worksiteIsSwedishAddress?.label ?? 'Svensk adress',
                value: WorksiteAddressCountry.Swedish,
                sortOrder: texts?.worksiteIsSwedishAddress?.radioSortOrder ?? 1,
              },
              {
                id: 'worksiteAddressCountryForeign',
                displayValue:
                  texts?.worksiteIsNotSwedishAddress?.label ??
                  'Utländsk adress',
                value: WorksiteAddressCountry.Foreign,
                sortOrder:
                  texts?.worksiteIsNotSwedishAddress?.radioSortOrder ?? 2,
              },
            ]}
          />
          <InputField
            name="worksiteAddress"
            label={texts?.worksiteAddress?.label ?? 'Arbetsställets adress'}
            placeholder={texts?.worksiteAddress?.placeholder ?? 'Ange adress'}
            required
          />
          <InputField
            name="worksitePostalCode"
            label={
              texts?.worksitePostalCode?.label ?? 'Arbetsställets postnummer'
            }
            placeholder={
              texts?.worksitePostalCode?.placeholder ?? 'Ange postnummer'
            }
            required
          />
          <InputField
            name="worksitePostalCity"
            label={texts?.worksitePostalCity?.label ?? 'Arbetsställets ort'}
            placeholder={texts?.worksitePostalCity?.placeholder ?? 'Ange ort'}
            required
          />
          <InputField
            name="worksiteTelephone"
            label={texts?.worksiteTelephone?.label ?? 'Arbetsställets telefon'}
            placeholder={
              texts?.worksiteTelephone?.placeholder ?? 'Ange telefonnummer'
            }
            required
          />
        </>
      )}
    </>
  );
};

export default WorksiteBlock;
